<template>
  <v-card flat>
    <v-card-text>
      <p v-if="isUser">{{ $t('alreadyUser') }}</p>

      <template v-else>
        <p >{{ $t('alreadyHelperText') }}</p>
        <v-form v-model="isEmailValid">
          <v-text-field
            :value="email"
            :error-messages="errorMessages.email"
            :label="$t('email')"
            :rules="[$rules.isValidEmail]"
            @input="changeEmailAndResetErrors"
            @keydown.enter.prevent="userifyOrDelete"/>
        </v-form>
      </template>

    </v-card-text>
    <v-card-actions>

      <v-spacer/>

      <confirm-dialog
        :is-open="accountDeleteDialog"
        :dialog-header="$t('deleteHeader')"
        :dialog-body="$t('deleteBody')"
        :is-loading="isLoading"
        :is-delete="true"
        :confirm-button-text="$t('delete')"
        @confirm="deleteEmployeeAccount"
        @dialogToggle="val => accountDeleteDialog = val"
      />

      <disabled-user-button v-if="isUser">
        <v-btn
          slot-scope="{isDisabledForUser}"
          :disabled="isDisabledForUser || accountDeleteDialog"
          color="error"
          @click.native="accountDeleteDialog = true"
        >
          {{ $t('deleteAccount') }}
        </v-btn>
      </disabled-user-button>

      <disabled-user-button v-else>
        <v-btn
          slot-scope="{isDisabledForUser}"
          :loading="isLoading"
          :disabled="isEmailInvalid || isDisabledForUser || isLoading || accountDeleteDialog"
          color="primary"
          @click.native="userifyEmployee"
        >
          {{ $t('userify') }}
        </v-btn>
      </disabled-user-button>

    </v-card-actions>
  </v-card>
</template>

<script>
import CompanyApi from '@/api/CompanyApi'

import { mapGetters, mapActions } from 'vuex'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'
import ConfirmDialog from '@/components/global-components/confirmDialog/ConfirmDialog'

export default {
  name: 'EmployeeSettingsUserify',
  components: { ConfirmDialog, DisabledUserButton },
  data () {
    return {
      isLoading: false,
      email: '',
      isEmailValid: false,
      accountDeleteDialog: false,
      errorMessages: {}
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee']),
    isUser () {
      if (this.currentEmployee) {
        return this.currentEmployee.user_id !== null
      }
    },
    isEmailEmpty () {
      return this.email.trim() === ''
    },
    isEmailInvalid () {
      return this.isEmailEmpty || !this.isEmailValid
    }
  },
  methods: {
    ...mapActions('employee', ['getEmployee']),
    changeEmailAndResetErrors (val) {
      this.email = val
      this.errorMessages = {}
    },
    async userifyOrDelete () {
      if (this.isUser) {
        await this.deleteEmployeeAccount()
      } else {
        await this.userifyEmployee()
      }
    },
    async userifyEmployee () {
      if (this.isEmailInvalid) return undefined

      try {
        this.isLoading = true
        const result = await this.toggleEmployeeAccountByAction('create', 'userifyEmployee')
        this.$notification(result)
        await this.updateEmployeeStore()
        this.changeEmailAndResetErrors('')
      } catch (e) {
        if (e.errors.message) {
          this.$notification(e)
        } else {
          this.errorMessages = e.errors
        }
      }
      this.isLoading = false
    },
    async deleteEmployeeAccount () {
      try {
        this.isLoading = true
        const result = await this.toggleEmployeeAccountByAction('delete', 'deleteEmployeeAccount')
        this.updateEmployeeStore()
        this.$notification(result)
      } catch (e) {
        this.$notification(e)
      }
      this.isLoading = false
      this.accountDeleteDialog = false
    },
    updateEmployeeStore () {
      return this.getEmployee({
        companyId: this.currentEmployee.company_id,
        employeeId: this.currentEmployee.id,
        force: true
      })
    },
    toggleEmployeeAccountByAction (can, action) {
      return CompanyApi[action](this.currentEmployee.id, action === 'userifyEmployee' ? this.email : null)
    }
  },
  i18n: {
    messages: {
      en: {
        deleteHeader: 'Remove user account',
        deleteBody: 'Should the user account of this user really be removed? ',
        alreadyHelperText: 'By creating a user account for this employee, the employee can independently record his working time. Enter the employee\'s e-mail address here and he will automatically be asked to set his password. Subsequently, the employee can use the system.',
        alreadyUser: 'The employee already has his own user account. You can also lock this again. No data is lost here. Only the user access is removed.',
        email: 'E-Mail',
        deleteAccount: 'Remove user account',
        userify: 'Create user account',
        delete: 'Delete'
      },
      tr: {
        deleteHeader: 'Kullanıcı hesabını kaldır',
        deleteBody: 'Kullanıcı hesabını kaldırmak istediğinize emin misiniz? ',
        alreadyHelperText: 'Bu çalışana kullanıcı hesabı tanımlandığında, çalışan bağımsız bir şekilde çalışma süresini kaydedebilecektir. Çalışanın e-posta adresini girdiğinizde, çalışanın şifre girişi otomatik olarak istenecektir. Sonrasında çalışan sistemi kullanabilecektir.',
        alreadyUser: 'Çalışanın daha önceden tanımlanmış bir kullanıcı hesabı bulunmaktadır. Bunu tekrar kilitleyebilirsiniz, herhangi bir veri kaybolmayacak olup yalnızca kullanıcı erişimi kaldırılacaktır.',
        email: 'E-posta',
        deleteAccount: 'Kullanıcı hesabını kaldır',
        userify: 'Kullanıcı hesabı tanımla',
        delete: 'Sil'
      },
      de: {
        deleteHeader: 'Benutzerkonto entfernen',
        deleteBody: 'Soll das Benutzerkonto dieses Benutzer wirklich entfernt werden? ',
        alreadyHelperText: 'Durch erstellen eines Benutzerkontos für diesen Mitarbeiter, kann der Mitarbeiter in Eigenverantwortung seine Arbeitszeit erfassen. Geben Sie hier die E-Mail Addresse des Mitarbeiters an und er bekommt eine automatische Aufforderung sein Passwort zu setzen. Anschließend, kann der Mitarbeiter das System nutzen. ',
        alreadyUser: 'Der Mitarbeiter hat bereits ein eigenes Benutzerkonto. Sie können dieses jedoch auch wieder sperren. Es gehen hierbei keine Daten verloren. Es wird lediglich der Benutzerzugang entfernt.  ',
        email: 'E-Mail',
        deleteAccount: 'Benutzerkonto entfernen',
        userify: 'Benutzerkonto erstellen',
        delete: 'Entfernen'
      },
      ru: {
        deleteHeader: 'Удалить учетную запись пользователя',
        deleteBody: 'Должна ли учетная запись этого пользователя действительно быть удалена?',
        alreadyHelperText: 'Создав учетную запись пользователя для этого сотрудника, сотрудник может самостоятельно записывать свое рабочее время. Введите адрес электронной почты сотрудника здесь, и он автоматически попросит установить свой пароль. Впоследствии сотрудник может использовать систему.',
        alreadyUser: 'У сотрудника уже есть собственная учетная запись. Вы также можете заблокировать это снова. Здесь данные не теряются. Только пользовательский доступ удален.',
        email: 'Эл. почта',
        deleteAccount: 'Удалить учетную запись пользователя',
        userify: 'Создать учетную запись пользователя',
        delete: 'Удалить'
      }
    }
  }
}
</script>
